/**
 * Phase 5-B — PulseTooltipController.
 *
 * Fires a floating speech-bubble next to the Pulse orb every 45-60s.
 * Rules per brief:
 *  - Starts the first timer ~15s after page load (not immediately).
 *  - Random 45-60s interval between tips.
 *  - Each tip auto-dismisses after 6s.
 *  - Mute toggle (localStorage-backed via usePulse) kills the loop.
 *  - Suppressed while drawer is open.
 *  - Route-aware: `/strategy` prefers strategy tips, `/dashboard` prefers
 *    library tips, otherwise default platform-insight pool.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { usePulse } from "@/contexts/PulseContext";

/* ----------------------------- Tip bank -------------------------------- */

const TIPS = [
    // Platform insights
    { id: "p-tt-tuesday", category: "platform", text: "Gastronomy Reels get 35% higher retention on Tuesdays — try one this week.", scope: ["home"] },
    { id: "p-ig-carousel", category: "platform", text: "Instagram Carousels drive 1.9× more saves than static images. Save = algorithm gold.", scope: ["home", "dashboard"] },
    { id: "p-tiktok-sound", category: "platform", text: "On TikTok, use a trending sound within 24h of its spike or move on — the half-life is brutal.", scope: ["home"] },
    { id: "p-yt-short-hook", category: "platform", text: "YouTube Shorts: your first frame IS the hook. Thumbnail = frame 1. Design it before you shoot.", scope: ["home"] },
    { id: "p-linkedin-formatting", category: "platform", text: "LinkedIn rewards whitespace. Short lines, 2-sentence paragraphs. Punch the first line like a hook.", scope: ["home"] },
    { id: "p-x-thread", category: "platform", text: "X threads: lead with the strongest insight. Burying it in reply 3 kills the whole thread.", scope: ["home"] },
    { id: "p-fb-hook", category: "platform", text: "Facebook rewards 2-3 sentence story hooks over quick jabs. Think micro-fiction, not punchlines.", scope: ["home"] },
    { id: "p-reddit-niche", category: "platform", text: "Reddit: niche subs convert 10× better than big ones. r/CookingForBeginners > r/food every time.", scope: ["home"] },
    { id: "p-devto-code", category: "platform", text: "Dev.to posts with a code block in the first screen keep readers 3× longer.", scope: ["home"] },
    { id: "p-tt-captions", category: "platform", text: "TikTok captions matter more than you think — the FYP ranks text relevance now, not just video content.", scope: ["home"] },

    // Workflow nudges
    { id: "w-qc-last", category: "workflow", text: "Have you tried running QC Review on your last post? Pre-publish audits catch 80% of flops.", scope: ["home"] },
    { id: "w-30d-strategy", category: "workflow", text: "Ready to generate your next 30-day strategy? Compound beats sporadic every time.", scope: ["home", "strategy"] },
    { id: "w-dashboard", category: "workflow", text: "Your Dashboard shows which copies worked. Open it. Pattern-match. Repeat the winners.", scope: ["home", "dashboard"] },
    { id: "w-strategy-tray", category: "workflow", text: "Already generated a strategy? Replay it from the Recent Strategies tray — no new charge.", scope: ["strategy"] },
    { id: "w-niche", category: "workflow", text: "Set your niche in NexaVault. Pulse gets 3× sharper when it knows what you're building.", scope: ["home"] },
    { id: "w-ascend", category: "workflow", text: "Running low on NXT? ASCEND tops you up with $5 → 500 NXT. Instant.", scope: ["home", "dashboard"] },
    { id: "w-adsum-apply", category: "workflow", text: "Pro tip: when Adsum suggests a tweak, Apply it — the one-click rewrite usually bumps the score by 10+.", scope: ["home"] },
    { id: "w-strategy-send", category: "workflow", text: "Sent a strategy to the Content Planner yet? It drops the whole roadmap into your library.", scope: ["strategy", "dashboard"] },
    { id: "w-copy-everywhere", category: "workflow", text: "Every suggestion in SmartPublish is one-click copyable. Don't retype. Ever.", scope: ["home", "dashboard", "strategy"] },
    { id: "w-plan-days", category: "workflow", text: "Shoot content for 3 days in one afternoon. Scheduling isn't publishing — give yourself a buffer.", scope: ["home", "strategy"] },

    // Trend hints
    { id: "t-ugc-trust", category: "trend", text: "UGC-style raw content outperforms polished for brand trust in 2026. The grain IS the signal.", scope: ["home", "strategy"] },
    { id: "t-vertical-first", category: "trend", text: "Vertical-first. 83% of social video is consumed vertical now. Shoot once, publish anywhere.", scope: ["home", "strategy"] },
    { id: "t-hook-under-3s", category: "trend", text: "You have 2.5 seconds to hook. If your pattern-interrupt isn't visible in frame 1, you lose.", scope: ["home"] },
    { id: "t-faceless-ok", category: "trend", text: "Faceless accounts in 2026 are growing faster than face-forward ones. Depth > identity.", scope: ["home", "strategy"] },
    { id: "t-live-sunday", category: "trend", text: "Sunday evenings 7-9pm local are the best Live/Audio slot for intimacy-seeking audiences.", scope: ["home", "strategy"] },
    { id: "t-remix-wins", category: "trend", text: "Remix other creators' content with your spin. Original content requires remix fuel.", scope: ["home"] },
    { id: "t-niche-micro", category: "trend", text: "Micro-niche > niche. 'Weeknight pasta for busy devs' > 'cooking content'.", scope: ["strategy", "home"] },
    { id: "t-reply-engage", category: "trend", text: "Reply to every comment in the first hour. Algorithms treat it as a strong engagement signal.", scope: ["home", "dashboard"] },
    { id: "t-collab-outlast", category: "trend", text: "Collabs with 2-3× smaller creators outlast collabs with 10× bigger ones. The reach curve is deceiving.", scope: ["home", "strategy"] },
    { id: "t-sunday-plan", category: "trend", text: "Sunday nights = planning. Draft Monday-Wednesday posts tonight, breathe easier all week.", scope: ["home", "strategy", "dashboard"] },
];

function pickTip(scope, lastId) {
    const pool = TIPS.filter((t) => t.scope.includes(scope));
    const base = pool.length > 0 ? pool : TIPS;
    const filtered = base.filter((t) => t.id !== lastId);
    const target = filtered.length > 0 ? filtered : base;
    return target[Math.floor(Math.random() * target.length)];
}

function routeScope(pathname) {
    if (pathname.startsWith("/strategy")) return "strategy";
    if (pathname.startsWith("/dashboard")) return "dashboard";
    return "home";
}

const START_DELAY_MS = 15000;
const MIN_INTERVAL_MS = 45000;
const MAX_INTERVAL_MS = 60000;
const AUTO_DISMISS_MS = 6000;

export default function PulseTooltipController({ orbSize = 56, orbPosition }) {
    const { drawerOpen, tipMuted } = usePulse();
    const location = useLocation();
    const [tip, setTip] = useState(null);
    const lastIdRef = useRef(null);
    const dismissTimerRef = useRef(null);
    const scheduleTimerRef = useRef(null);

    const scope = useMemo(() => routeScope(location.pathname), [location.pathname]);

    const clearTimers = () => {
        if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = null;
        }
        if (scheduleTimerRef.current) {
            clearTimeout(scheduleTimerRef.current);
            scheduleTimerRef.current = null;
        }
    };

    const showTip = () => {
        const next = pickTip(scope, lastIdRef.current);
        if (!next) return;
        lastIdRef.current = next.id;
        setTip(next);
        dismissTimerRef.current = setTimeout(() => setTip(null), AUTO_DISMISS_MS);
    };

    const scheduleNext = (delay) => {
        clearTimers();
        scheduleTimerRef.current = setTimeout(() => {
            if (!drawerOpen && !tipMuted) {
                showTip();
            }
            scheduleNext(
                MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS)
            );
        }, delay);
    };

    useEffect(() => {
        if (tipMuted || drawerOpen) {
            clearTimers();
            setTip(null);
            return () => clearTimers();
        }
        scheduleNext(START_DELAY_MS);
        return () => clearTimers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipMuted, drawerOpen, scope]);

    // Suppress proactively if drawer is opened mid-tip.
    useEffect(() => {
        if (drawerOpen && tip) setTip(null);
    }, [drawerOpen, tip]);

    if (!tip || !orbPosition) return null;

    // Position the bubble to the LEFT of the orb; snap above if the orb is
    // near the top edge of the viewport.
    const bubbleTop = orbPosition.y + orbSize / 2;
    const bubbleRight =
        typeof window !== "undefined"
            ? window.innerWidth - orbPosition.x + 12
            : orbSize + 12;

    return (
        <AnimatePresence>
            <motion.div
                key={tip.id}
                initial={{ opacity: 0, x: 10, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 10, scale: 0.95 }}
                transition={{ duration: 0.25 }}
                className="pointer-events-none fixed z-40 flex max-w-[320px] -translate-y-1/2 items-start gap-2 rounded-xl border border-[#6366F1]/40 bg-[#0B1220] px-3.5 py-2.5 text-xs text-[#E8ECF5] shadow-[0_12px_32px_rgba(0,0,0,0.5)] sm:max-w-[360px]"
                style={{
                    right: bubbleRight,
                    top: bubbleTop,
                }}
                data-testid="pulse-tooltip-bubble"
                data-tip-id={tip.id}
            >
                <Sparkles
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#00F5D4]"
                    strokeWidth={2.5}
                />
                <span className="leading-relaxed">{tip.text}</span>
            </motion.div>
        </AnimatePresence>
    );
}
